import { useCallback, useMemo } from "react";

import { useHistory } from "react-router-dom";
import { AppModel, AssetModelBillingAnomalyModel, CommitmentManagersModel, TimeInterval } from "@doitintl/cmp-models";
import { getCollection, getCollectionGroup } from "@doitintl/models-any";
import { useCollectionData } from "@doitintl/models-hooks";
import sum from "lodash/sum";
import { DateTime } from "luxon";

import { type CardID } from "../../../Components/Dashboard/CloudSpendSummaryCards/types";
import { useCloudSummaryReportLinks } from "../../../Components/Dashboard/CloudSpendSummaryCards/useCloudSummaryReportLinks";
import { useBudgets } from "../../../Components/hooks/cloudAnalytics/budgets/useBudgets";
import { useAuthContext } from "../../../Context/AuthContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { useUserContext } from "../../../Context/UserContext";
import { getCostOfAnomaly } from "../../../Pages/AnomalyDetection/AlertTools";
import { DEFAULT_BUDGET_AMOUNT } from "../../../Pages/CloudAnalytics/budgets/const";
import { getDataRowsValues } from "../../../Pages/CloudAnalytics/budgets/utils";
import { BudgetTypes } from "../../../Pages/CloudAnalytics/utilities";
import { useInsights } from "../../../Pages/Insights/hooks";
import { Category } from "../../../Pages/Insights/types";
import { totalPotentialDailySavings } from "../../../Pages/Insights/utils";
import { type AnomalyWithCostAndRef, type ParsedBudget } from "./types";
import { getTotalPotentialSavings } from "./utils";

export const useCommitments = () => {
  const { customer } = useCustomerContext();
  const [commitmentManagers, loading] = useCollectionData(
    getCollection(CommitmentManagersModel).where("customerId", "==", customer.id)
  );

  const { activeCommitments, inactiveCommitments } = useMemo(() => {
    if (!commitmentManagers) {
      return { activeCommitments: [], inactiveCommitments: [] };
    }

    const now = DateTime.now().toUTC();
    const active: CommitmentManagersModel[] = [];
    const inactive: CommitmentManagersModel[] = [];

    commitmentManagers.forEach((commitment) => {
      if (!commitment.periods || commitment.periods.length === 0) {
        return;
      }

      const lastPeriod = commitment.periods[commitment.periods.length - 1];
      const endDate = DateTime.fromJSDate(lastPeriod.endDate.toDate()).toUTC();

      if (endDate < now) {
        inactive.push(commitment);
      } else {
        active.push(commitment);
      }
    });

    return { activeCommitments: active, inactiveCommitments: inactive };
  }, [commitmentManagers]);

  return { activeCommitments, inactiveCommitments, loading };
};

export const useAnomalies = () => {
  const { customerOrPresentationModeCustomer } = useCustomerContext();

  const query = useMemo(
    () =>
      getCollectionGroup(AssetModelBillingAnomalyModel)
        .where("customer", "==", customerOrPresentationModeCustomer.ref)
        .where("timestamp", ">=", DateTime.now().minus({ days: 90 }).toUTC().toJSDate())
        .orderBy("timestamp", "desc"),
    [customerOrPresentationModeCustomer.ref]
  );

  const [data, loading] = useCollectionData(query, {
    refField: "ref",
  });

  const anomaliesWithCost: AnomalyWithCostAndRef[] = useMemo(() => {
    if (!data) {
      return [];
    }
    const withCost = data.map((anomaly) => ({
      ...anomaly,
      cost: getCostOfAnomaly(anomaly),
    }));

    return withCost;
  }, [data]);

  const activeAnomalies = useMemo(() => {
    if (!anomaliesWithCost) {
      return [];
    }
    return anomaliesWithCost.filter((anomaly) => anomaly.status === "ACTIVE");
  }, [anomaliesWithCost]);

  const allAnomalies: AnomalyWithCostAndRef[] = useMemo(() => {
    if (!anomaliesWithCost) {
      return [];
    }

    const activeAnomaliesOrderedByCost = activeAnomalies.sort((a, b) => b.cost - a.cost);

    const inactiveAnomalies = anomaliesWithCost.filter((anomaly) => anomaly.status === "INACTIVE");
    const inactiveAnomaliesOrderedByCost = inactiveAnomalies.sort((a, b) => b.cost - a.cost);

    return [...activeAnomaliesOrderedByCost, ...inactiveAnomaliesOrderedByCost];
  }, [anomaliesWithCost, activeAnomalies]);

  const totalCostAtRisk = useMemo(() => {
    if (!activeAnomalies) {
      return 0;
    }

    return activeAnomalies.reduce((acc, anomaly) => acc + anomaly.cost, 0);
  }, [activeAnomalies]);

  const thirtyDaysAgo = useMemo(() => DateTime.now().minus({ days: 30 }).toUTC(), []);

  const numberOfAnomaliesInLast30Days = useMemo(
    () =>
      activeAnomalies?.filter((anomaly) => {
        const anomalyDate = anomaly.timestamp.toDate();
        return anomalyDate > thirtyDaysAgo.toJSDate();
      }).length,
    [activeAnomalies, thirtyDaysAgo]
  );

  const anomaliesCostAtRiskInLast7Days = useMemo(() => {
    if (!activeAnomalies) {
      return 0;
    }

    const sevenDaysAgo = DateTime.now().minus({ days: 7 }).toUTC();

    return activeAnomalies.reduce((acc, anomaly) => {
      const anomalyDate = anomaly.timestamp.toDate();
      if (anomalyDate > sevenDaysAgo.toJSDate()) {
        return acc + anomaly.cost;
      }
      return acc;
    }, 0);
  }, [activeAnomalies]);

  return {
    activeAnomalies,
    loading,
    totalCostAtRisk,
    numberOfAnomaliesInLast30Days,
    allAnomalies,
    anomaliesCostAtRiskInLast7Days,
  };
};

export const useActiveBudgets = () => {
  const [_, rawBudgets, loading] = useBudgets();

  const budgets: ParsedBudget[] = useMemo(
    () =>
      rawBudgets.map((b) => {
        const budgetAmountToDate =
          b.data.config?.amount && !isNaN(b.data.config.amount) ? b.data.config.amount : DEFAULT_BUDGET_AMOUNT;
        const forecastRows = b.data.data?.forecastRows ?? null;
        const shouldUseForecastRows = forecastRows && Object.keys(forecastRows).length > 0;
        const dataTimeInterval = b.data?.data?.timeInterval ?? TimeInterval.MONTH;
        const forecastRowsBudgetData = getDataRowsValues(forecastRows, dataTimeInterval, 1, false, false);
        const forecastToUse = shouldUseForecastRows ? forecastRowsBudgetData : [];

        const forecasted = b.data.utilization?.forecasted ?? 0;
        const forecastAmountForEntirePeriod =
          forecastToUse.length && b.data.config.type === BudgetTypes.RECURRING ? forecasted : null;

        return {
          budgetAmountToDate,
          budgetAmount: b.data.config.amount,
          forecasted: forecastAmountForEntirePeriod ?? 0,
          actualAmountToDate: b.data.utilization?.current ?? 0,
          createdAt: b.data.timeCreated.toDate(),
          name: b.data.name,
          timeWhenTotalAmountExceeded: b.data.utilization?.forecastedTotalAmountDate,
          id: b.id,
        };
      }),
    [rawBudgets]
  );

  const totalCostAtRisk = useMemo(
    () =>
      budgets.reduce((acc, budget) => {
        const budgetAtRisk = budget.forecasted - budget.budgetAmount;
        if (budgetAtRisk > 0) {
          return acc + budgetAtRisk;
        }
        return acc;
      }, 0),
    [budgets]
  );

  const budgetsNearThreshold = useMemo(
    () => budgets.filter((budget) => budget.actualAmountToDate > budget.budgetAmount * 0.5),
    [budgets]
  );

  const orderedBudgets = useMemo(
    () =>
      budgets.sort((a, b) => {
        const currentUsageA = a.budgetAmount === 0 ? 0 : (a.actualAmountToDate / a.budgetAmount) * 100;
        const currentUsageB = b.budgetAmount === 0 ? 0 : (b.actualAmountToDate / b.budgetAmount) * 100;
        return currentUsageB - currentUsageA;
      }),
    [budgets]
  );

  const budgetsCostAtRiskInLast7Days = useMemo(
    () =>
      budgets.reduce((acc, budget) => {
        const sevenDaysAgo = DateTime.now().minus({ days: 7 }).toUTC().toJSDate();
        const budgetAtRisk = budget.forecasted - budget.budgetAmount;
        if (budgetAtRisk > 0 && budget.createdAt > sevenDaysAgo) {
          return acc + budgetAtRisk;
        }
        return acc;
      }, 0),
    [budgets]
  );
  return { totalCostAtRisk, budgetsNearThreshold, loading, budgets: orderedBudgets, budgetsCostAtRiskInLast7Days };
};

export const useInsightsPotentialSavings = () => {
  const { insights, isLoading, noAccessResults } = useInsights();

  const sevenDaysAgo = useMemo(() => DateTime.now().minus({ days: 7 }).toUTC().toJSDate(), []);

  const noAccessInsightsTotalPotentialSavings = useMemo(() => {
    if (!noAccessResults) {
      return 0;
    }
    const notOptimizedInsights = noAccessResults.filter(
      (insight) => insight.potentialDailySavings?.isOptimized === false
    );

    const potentialSavingsPerInsight = notOptimizedInsights.map(
      (result) => (result.potentialDailySavings?.value || 0) * 365
    );

    return sum(potentialSavingsPerInsight);
  }, [noAccessResults]);

  const actionableInsights = useMemo(
    () =>
      insights?.filter(
        (insight) => insight.displayStatus === "actionable" && insight.results?.potentialDailySavings?.breakdown
      ),
    [insights]
  );

  const insightsOfLast7Days = useMemo(() => {
    if (!actionableInsights) {
      return [];
    }
    return actionableInsights.filter((insight) => DateTime.fromISO(insight.lastUpdated).toJSDate() > sevenDaysAgo);
  }, [actionableInsights, sevenDaysAgo]);

  const insightsCostAtRiskInLast7Days = useMemo(
    () => getTotalPotentialSavings(insightsOfLast7Days || []),
    [insightsOfLast7Days]
  );

  const totalPotentialSavings = useMemo(() => getTotalPotentialSavings(actionableInsights || []), [actionableInsights]);

  const cloudFlows = useMemo(
    () => actionableInsights?.filter((insight) => insight.cloudFlowBlueprintId),
    [actionableInsights]
  );

  const cloudFlowsCount = useMemo(() => cloudFlows?.length ?? 0, [cloudFlows]);

  const cloudFlowsRisk = useMemo(() => getTotalPotentialSavings(cloudFlows || []), [cloudFlows]);

  const orderedInsights = useMemo(() => {
    if (!actionableInsights) {
      return [];
    }
    return actionableInsights.sort((a, b) => {
      // First, sort by priority (High > Medium > Low)
      const priorityA = a.priority === "High" ? 3 : a.priority === "Medium" ? 2 : a.priority === "Low" ? 1 : 0;
      const priorityB = b.priority === "High" ? 3 : b.priority === "Medium" ? 2 : b.priority === "Low" ? 1 : 0;

      if (priorityA !== priorityB) {
        return priorityB - priorityA;
      }

      // For insights with the same priority (or no priority), sort by potential savings
      const savingsA = totalPotentialDailySavings(a) || 0;
      const savingsB = totalPotentialDailySavings(b) || 0;
      return savingsB - savingsA;
    });
  }, [actionableInsights]);

  const securityInsights = useMemo(() => {
    if (!insights) {
      return [];
    }
    return insights.filter((insight) => insight?.categories?.includes(Category.Security));
  }, [insights]);

  if (!insights && noAccessResults) {
    return {
      totalPotentialSavings: noAccessInsightsTotalPotentialSavings,
      isLoading,
      cloudFlowsCount: 0,
      cloudFlowsRisk: 0,
      insightsCostAtRiskInLast7Days: 0,
      allInsights: [],
      securityInsights: [],
    };
  }

  return {
    totalPotentialSavings,
    isLoading,
    cloudFlowsCount,
    cloudFlowsRisk,
    insightsCostAtRiskInLast7Days,
    allInsights: orderedInsights,
    securityInsights,
  };
};

export const useActiveCommitments = () => {
  const { activeCommitments, loading } = useCommitments();

  const now = useMemo(() => DateTime.now().toUTC().toJSDate(), []);

  return useMemo(() => {
    let commitmentsCostAtRisk = 0;
    let activeCommitmentsAtRiskCount = 0;
    let totalCommitment = 0;

    activeCommitments.forEach((commitment) => {
      for (const [index, period] of commitment.periods.entries()) {
        if (period.endDate.toDate() < now || period.startDate.toDate() > now) {
          continue;
        }

        const endOfPeriodForecast =
          commitment.periodsSpend?.[index]?.forecastSeries?.[commitment.periodsSpend[index].forecastSeries.length - 1]
            ?.value;

        const commitmentAmount = period.commitmentValue;
        totalCommitment += commitmentAmount;

        if (endOfPeriodForecast !== undefined && endOfPeriodForecast < commitmentAmount) {
          commitmentsCostAtRisk += commitmentAmount - endOfPeriodForecast;
          activeCommitmentsAtRiskCount++;
        }
      }
    });

    return {
      commitmentsCostAtRisk,
      activeCommitmentsAtRiskCount,
      loading,
      totalCommitment,
      totalAmountOfActiveCommitments: activeCommitments.length,
    };
  }, [activeCommitments, loading, now]);
};

export const useZendeskTickets = () => {
  const { customer } = useCustomerContext();
  const { userModel } = useUserContext({ allowNull: false });
  const { isDoitEmployee } = useAuthContext({ mustHaveUser: true });
  const customerRef = customer.ref;

  let baseQuery = getCollection(AppModel)
    .doc("support")
    .collection("tickets")
    .where("customerRef", "==", customerRef)
    .where("ticket.fields.Platform", "!=", "finance___billing")
    .where("ticket.is_public", "==", true)
    .orderBy("ticket.created_at", "desc")
    .limit(20);

  if (!isDoitEmployee && !customer?.zendeskOrganizationInfo?.sharedTickets) {
    baseQuery = baseQuery.where("ticket.requester_email", "==", userModel.email);
  }

  const [tickets, loading] = useCollectionData(baseQuery, {
    refField: "ref",
  });

  const filteredTickets = useMemo(
    () => (tickets ?? []).filter((t) => !t.ticket.description.includes("created a new chat called")),
    [tickets]
  );
  const orderedTickets = useMemo(
    () =>
      filteredTickets.sort((a, b) => b.ticket.created_at.toDate().getTime() - a.ticket.created_at.toDate().getTime()),
    [filteredTickets]
  );

  return { tickets: orderedTickets, loading };
};

export const useIsSupportUser = () => {
  const { userRoles } = useUserContext({ requiredRoles: true });

  return userRoles.roleName() === "Support User";
};

export const useOnNumberClick = () => {
  const history = useHistory();
  const { loadLinks } = useCloudSummaryReportLinks();

  const onNumberClick = useCallback(
    async (cardId: CardID | undefined) => {
      if (cardId === undefined) {
        return;
      }
      const links = await loadLinks(cardId);
      if (links[cardId]) {
        history.push(links[cardId]);
      }
    },
    [history, loadLinks]
  );

  return { onNumberClick };
};
